/**
 * Grouping of solver decision variables into "tables".
 *
 * Each variable in a {@link Model} may carry a `group` (the table it belongs to,
 * e.g. "P_gen") and `indices` (the values of that table's index columns, e.g.
 * `{ gen_id: "gen1", timestamp: 0 }`). This module joins those variables with a
 * HiGHS solution to produce, per group, a table whose rows are the index values
 * augmented with the solved value and status.
 */

import { Variable, HighsRawSolution, IndexValue } from './highs';

/** One solved variable within a group: its index values plus value and status. */
export interface SolutionVariableRow {
  /** Underlying variable name (used as a stable React key). */
  name: string;
  /** Index column values, keyed by column name. */
  indices: Record<string, IndexValue>;
  /** Solved primal value, or undefined if the variable is absent from the solution. */
  value?: number;
  /** HiGHS column status (e.g. "LB", "UB", "BS"), if available. */
  status?: string;
}

/** A group of variables sharing the same index columns, e.g. "P_gen". */
export interface SolutionVariableTable {
  /** Group name, e.g. "P_gen" or "P_line". */
  name: string;
  /** Ordered index column names, e.g. ["gen_id", "timestamp"]. */
  columns: string[];
  rows: SolutionVariableRow[];
}

/** Fallback group and column for variables that carry no grouping metadata. */
const UNGROUPED_TABLE = 'other';
const UNGROUPED_COLUMN = 'variable';

/**
 * Join a model's variables with a solution, grouping them into tables.
 *
 * Variables are bucketed by their `group`; within each table the columns are the
 * ordered union of index keys seen, and rows are sorted by their index values.
 * Variables without grouping metadata fall into an "other" table keyed by name,
 * so nothing is silently dropped.
 */
export function extractSolutionTables(
  variables: Variable[],
  solution?: HighsRawSolution
): SolutionVariableTable[] {
  const columns = solution?.Columns;

  // Preserve first-seen order of both groups and their columns.
  const tables = new Map<string, SolutionVariableTable>();

  for (const variable of variables) {
    const groupName = variable.group ?? UNGROUPED_TABLE;
    const indices: Record<string, IndexValue> =
      variable.group !== undefined && variable.indices
        ? variable.indices
        : { [UNGROUPED_COLUMN]: variable.name };

    let table = tables.get(groupName);
    if (!table) {
      table = { name: groupName, columns: [], rows: [] };
      tables.set(groupName, table);
    }

    for (const column of Object.keys(indices)) {
      if (!table.columns.includes(column)) {
        table.columns.push(column);
      }
    }

    const columnData = columns?.[variable.name];
    table.rows.push({
      name: variable.name,
      indices,
      value: columnData?.Primal,
      status: columnData?.Status,
    });
  }

  const result = Array.from(tables.values());
  for (const table of result) {
    table.rows.sort((a, b) => compareByColumns(a.indices, b.indices, table.columns));
  }
  return result;
}

/** Order rows by their index columns: numbers numerically, everything else lexically. */
function compareByColumns(
  a: Record<string, IndexValue>,
  b: Record<string, IndexValue>,
  columns: string[]
): number {
  for (const column of columns) {
    const av = a[column];
    const bv = b[column];
    if (av === bv) continue;
    if (typeof av === 'number' && typeof bv === 'number') {
      return av - bv;
    }
    return String(av ?? '').localeCompare(String(bv ?? ''));
  }
  return 0;
}
